import { Icon, Loader, Toast } from '@modulor/react';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import { useNavigateWithSearch } from 'Modulor/hooks';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useLazyContentPageQuery } from 'store/services/contentPageAPI';
import { resetCarouselFormLoadedOnce, setCarouselViewOff } from 'store/slices/carousel';
import { contentPageUrlSelector, resetContentPageUrl } from 'store/slices/contentPage';
import { brandSelector, langSelector, userIdSelector } from 'store/slices/global';
import { deinlineStyles, formContent, getContent } from 'utils';

import { useSetHeaderConfig } from '../components/Header/hooks/useSetHeaderConfig';

export const ContentController = () => {
  const { translate } = useSkeletonTranslation();
  const dispatch = useAppDispatch();
  const brand = useAppSelector(brandSelector);
  const lang = useAppSelector(langSelector).toLowerCase();
  const url = useAppSelector(contentPageUrlSelector);
  const userId = useAppSelector(userIdSelector);
  const [trigger, result] = useLazyContentPageQuery();
  const { data = [], isFetching, isError, error } = result;
  const [content, setContent] = useState<string>('');
  const navigate = useNavigateWithSearch();

  useSetHeaderConfig({
    preset: 'content',
    onNavigationAction: () => {
      dispatch(setCarouselViewOff());
      dispatch(resetCarouselFormLoadedOnce());
      navigate(-1);
    },
  });

  useEffect(() => {
    if (url) {
      trigger({
        url,
        userId,
      });
    }

    return () => {
      dispatch(resetContentPageUrl());
    };
  }, [url, userId]);

  useEffect(() => {
    if (!isFetching && !!url && data.length) {
      const currentContent = getContent(data, lang, brand);
      const rawHtml = formContent(currentContent, userId);
      const nonce = window.__webpack_nonce__;
      const htmlContent = deinlineStyles(rawHtml, nonce);
      setContent(htmlContent);
    }
  }, [isFetching, url, data]);

  return (
    <>
      {!!url && isError && (
        <Toast
          variant="error"
          visible={Boolean(isError)}
          text={translate(error.toString())}
          multiline
          rightIcon={<Icon name="close" />}
          duration={2500}
          onHide={() => {}}
        />
      )}
      <div className="content-page-wrapper">
        {isFetching && <Loader />}
        <div
          id="content-anchor"
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      </div>
    </>
  );
};
